import { c as _c } from "react/compiler-runtime";
import { IcoInfo24, IcoWarning24 } from '@onefootprint/icons';
import type { ErrorDetail } from '@onefootprint/request-types/dashboard';
import { useShortcut } from '@onefootprint/ui';
import * as RadixPopover from '@radix-ui/react-popover';
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMobileLayout from '@/hooks/use-mobile-layout';
import useDirty from '../../hooks/use-dirty';
import useSidebar from '../../hooks/use-sidebar';
import ErrorSheet from './components/error-warning-sheet';
const OPEN_ERRORS_SHORTCUT = ['ctrl', 'e'];
const CLOSE_ERRORS_SHORTCUT = ['ctrl', 'c'];
export type Banner = {
  description: ErrorDetail;
  nodeId: string;
  type: 'error' | 'warning' | 'info';
};
type ErrorWarningListProps = {
  items: Banner[];
};
const ErrorWarningList = t0 => {
  const $ = _c(11);
  const {
    items
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const isMobile = useMobileLayout();
  const [openOverride, setOpenOverride] = useState(null);
  const isOpen = openOverride ?? !isMobile;
  const {
    open: openNode
  } = useSidebar();
  const {
    isAnyComponentDirty
  } = useDirty();
  let t1;
  if ($[0] !== isAnyComponentDirty || $[1] !== openNode) {
    t1 = nodeId => {
      if (isAnyComponentDirty) {
        return;
      }
      openNode(nodeId);
    };
    $[0] = isAnyComponentDirty;
    $[1] = openNode;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleClickError = t1;
  let t2;
  if ($[3] !== isOpen) {
    t2 = {
      keys: CLOSE_ERRORS_SHORTCUT,
      onEscKeyDown() {
        if (isOpen) {
          setOpenOverride(false);
        }
      },
      disabled: !isOpen
    };
    $[3] = isOpen;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useShortcut(t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keys: OPEN_ERRORS_SHORTCUT,
      onShortcut() {
        setOpenOverride(true);
      }
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useShortcut(t3);
  let t4;
  if ($[6] !== handleClickError || $[7] !== isOpen || $[8] !== items || $[9] !== t) {
    const hasErrors = items.some(_temp);
    const hasWarnings = items.some(_temp2);
    const hasInfo = items.some(_temp3);
    const handleOutsideClick = _temp4;
    const getTitle = () => {
      if (hasErrors && hasWarnings && hasInfo) {
        return t(_temp5);
      }
      if (hasErrors && hasWarnings) {
        return t(_temp6);
      }
      if (hasErrors && hasInfo) {
        return t(_temp7);
      }
      if (hasWarnings && hasInfo) {
        return t(_temp8);
      }
      if (hasErrors) {
        return t(_temp9);
      }
      if (hasWarnings) {
        return t(_temp10);
      }
      if (hasInfo) {
        return t(_temp11);
      }
      return "";
    };
    const isInfoOnly = !hasErrors && !hasWarnings && hasInfo;
    t4 = hasErrors || hasWarnings || hasInfo ? <RadixPopover.Root open={isOpen} onOpenChange={setOpenOverride}><RadixPopover.Trigger asChild={true}><button type="button" className={cx("absolute z-popover flex items-center gap-1.5 py-1.5 pl-1.5 pr-3 rounded-full cursor-pointer bottom-4 left-14 max-md:bottom-[max(1rem,env(safe-area-inset-bottom))] before:content-[''] before:absolute before:inset-0 before:bg-gray-900/10 before:backdrop-screen before:rounded-full before:opacity-0 before:transition-opacity hover:before:opacity-100", {
          "bg-error-inverted": hasErrors,
          "bg-warning-inverted": !hasErrors && hasWarnings,
          "bg-secondary border border-tertiary": isInfoOnly
        })}><div className={cx("flex relative justify-center items-center p-1 rounded-full")}>{hasErrors ? <IcoWarning24 color="error" /> : hasWarnings ? <IcoWarning24 color="warning" /> : <IcoInfo24 color="secondary" />}</div><span className={cx("text-label-2 max-md:sr-only", isInfoOnly ? "text-secondary" : "text-quinary")}>{getTitle()}</span><span className={cx("text-label-2", isInfoOnly ? "text-secondary" : "text-quinary")}>({items.length})</span></button></RadixPopover.Trigger><RadixPopover.Portal><RadixPopover.Content onPointerDownOutside={handleOutsideClick} onInteractOutside={handleOutsideClick} align="start" sideOffset={8} forceMount={true}><ErrorSheet errors={items} open={handleClickError} isOpen={isOpen} /></RadixPopover.Content></RadixPopover.Portal></RadixPopover.Root> : null;
    $[6] = handleClickError;
    $[7] = isOpen;
    $[8] = items;
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
export default ErrorWarningList;
function _temp(item) {
  return item.type === "error";
}
function _temp2(item_0) {
  return item_0.type === "warning";
}
function _temp3(item_1) {
  return item_1.type === "info";
}
function _temp4(e) {
  e.preventDefault();
  e.stopPropagation();
}
function _temp5($) {
  return $.errorList.titleErrorsWarningsAndInfo;
}
function _temp6($_0) {
  return $_0.errorList.titleErrorsAndWarnings;
}
function _temp7($_1) {
  return $_1.errorList.titleErrorsAndInfo;
}
function _temp8($_2) {
  return $_2.errorList.titleWarningsAndInfo;
}
function _temp9($_3) {
  return $_3.errorList.titleErrorsOnly;
}
function _temp10($_4) {
  return $_4.errorList.titleWarningsOnly;
}
function _temp11($_5) {
  return $_5.errorList.titleInfoOnly;
}